/**
 * crossThreadSearch/textCore — the PURE text core of the cross-thread search
 * (tokenizer, bilingual stopwords, idf-ish scorer, snippet window), split out
 * of index.ts on 2026-09-09 so the thread search box's corpus
 * (services/threadSearch/searchThreadCorpus.ts) and the search UI can import
 * it without dragging Firebase into a jsdom test. index.ts re-exports every
 * symbol, so nothing that imported from there moved. The server's
 * threadSearchCore.ts is a verbatim port of THIS file (parity-pinned by
 * tests/groupThreads/threadSearchCorpus.test.ts).
 */

/** Lowercase, strip punctuation, split on whitespace. Bilingual-friendly. */
export function tokenize(text: string): string[] {
  if (!text) return [];
  return text
    .toLowerCase()
    // Use NFC so composed accented characters (e.g. é) stay as a single
    // code-point that survives the regex below. NFKD splits accents off
    // into combining marks which then get scrubbed and we lose the
    // accent on the final token — something Spanish-language search
    // care a great deal about.
    .normalize('NFC')
    // Replace anything that's not a letter (incl. accented), digit, or
    // hyphen-inside-word with a space. \p{L} keeps Spanish accents,
    // CJK, etc.
    .replace(/[^\p{L}\p{N}-]+/gu, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 2);
}

const STOPWORDS_EN = new Set([
  'the','and','for','that','this','with','was','were','are','have','has','had',
  'you','your','our','their','his','her','him','she','they','them','from',
  'what','when','where','which','while','will','would','could','should','about',
  'into','than','then','these','those','some','such','any','all','also','too',
  'but','can','did','does','done','just','last','many','more','most','only',
  'over','same','very','well','were','than','tha',
]);
const STOPWORDS_ES = new Set([
  'que','con','los','las','del','para','por','como','este','esta','esto','estos',
  'estas','sus','sin','sobre','entre','muy','pero','sino','desde','hasta','ya',
  'mas','aun','aunque','poco','algo','aqui','alli','solo','todo','toda','todos',
  'todas','tan','tanto','tanta','asi','bien','mal','sea','son','soy','eres','era',
  'eran','fue','fueron','ser','estar','estaba','estaban','estoy','estas','esta',
  'tiene','tienen','tenia','tener','hay','han','habia','habian','haber','hacer',
  'hace','hacen','hacia','hacian','hizo','hicieron','tras','siempre','nunca',
  'tal','aunque','porque','mientras','cuando','donde','quien','cuyo','cuya',
  'una','uno','unas','unos',
]);

export function isStopword(token: string): boolean {
  return STOPWORDS_EN.has(token) || STOPWORDS_ES.has(token);
}

/**
 * Score a candidate document against a tokenized query.
 *
 * Scoring is a simple sum of (idf-ish weight \u00d7 occurrence) per overlapping
 * token. We weight rare tokens higher by 1 / (1 + log(df)) where df is the
 * pre-computed document-frequency across the corpus. This is enough to
 * surface "workers' comp" matches over generic "the" matches without the
 * complexity of full TF/IDF.
 */
export function scoreDocument(
  queryTokens: string[],
  docTokens: string[],
  documentFrequencies: Map<string, number>,
): number {
  if (queryTokens.length === 0 || docTokens.length === 0) return 0;
  const docTokenSet = new Set(docTokens);
  let score = 0;
  for (const qt of queryTokens) {
    if (!docTokenSet.has(qt)) continue;
    const df = documentFrequencies.get(qt) || 1;
    const weight = 1 / (1 + Math.log(df + 1));
    // Count occurrences for a small TF boost (capped to 5 to prevent spam).
    let occ = 0;
    for (const dt of docTokens) if (dt === qt) { occ += 1; if (occ >= 5) break; }
    score += weight * occ;
  }
  return score;
}

// ────────────────────────────────────────────────────────────────────────────
// Snippet extraction
// ────────────────────────────────────────────────────────────────────────────

/**
 * Pull a short window from `text` centred on the first matching token.
 * Falls back to the leading window if no token matches (defensive \u2014 should
 * never happen since callers gate on score > 0).
 */
export function extractSnippet(
  text: string,
  queryTokens: string[],
  maxChars: number,
): string {
  if (!text) return '';
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= maxChars) return trimmed;
  const lower = trimmed.toLowerCase();
  let bestIdx = -1;
  for (const qt of queryTokens) {
    const i = lower.indexOf(qt);
    if (i !== -1 && (bestIdx === -1 || i < bestIdx)) bestIdx = i;
  }
  if (bestIdx === -1) {
    return trimmed.slice(0, maxChars - 1) + '\u2026';
  }
  // Centre the window on the match, clamped to bounds.
  const half = Math.floor((maxChars - 2) / 2);
  let start = Math.max(0, bestIdx - half);
  let end = Math.min(trimmed.length, start + maxChars - 2);
  // If end hit the right edge, shift start back so we use the full budget.
  start = Math.max(0, end - (maxChars - 2));
  const prefix = start > 0 ? '\u2026' : '';
  const suffix = end < trimmed.length ? '\u2026' : '';
  return prefix + trimmed.slice(start, end).trim() + suffix;
}
